import React from 'react';
import { Bot, Waves, Brain, Dumbbell, Shield, Check } from 'lucide-react';
import MarketingPage, { ContentCard } from '@/components/marketing/MarketingPage';
import { pageJsonLd, faqJsonLd } from '@/components/shared/Seo';

const FAQ = [
  { q: 'What is Jay?',
    a: 'Jay is Numuv\u2019s AI interviewer. Jay conducts structured video and audio screening interviews on demand, transcribes every answer with speaker labels and timestamps, and scores competencies to produce a job-fit report.' },
  { q: 'Does Jay decide who gets hired?',
    a: 'No. Jay surfaces signals and scores, never verdicts. Recruiters own every decision. An optional per-job job-fit threshold can auto-select or auto-reject, but it is opt-in and configured by the recruiter.' },
  { q: 'How does Numuv keep AI interviews fair?',
    a: 'Every candidate for a role answers role-calibrated questions, scores cite the exact answer they came from, and a mandatory warm-up check plus anti-cheat rules apply equally. Recordings and transcripts are retained so any score can be reviewed by a human.' },
  { q: 'Can candidates practise with Jay before a real interview?',
    a: 'Yes. The interview prep simulator generates role- and company-aware questions with no repeats, transcribes voice answers, and returns a scorecard with feedback on content and communication.' },
];

const JSONLD = pageJsonLd({
  name: 'Meet Jay, the Numuv AI interviewer',
  description: 'Jay conducts structured AI video and audio interviews on demand, transcribes and scores every answer, and produces objective, bias-aware reports for recruiters.',
  path: '/meet-jay',
  breadcrumbs: [{ name: 'Meet Jay', path: '/meet-jay' }],
  extra: [faqJsonLd(FAQ)],
});

export default function MeetJay() {
  return (
    <MarketingPage
      jsonLd={JSONLD}
      faq={FAQ}
      seoTitle="Meet Jay — the AI interviewer that never sleeps"
      seoDescription="Jay is Numuv's AI interviewer. Structured video and audio interviews on demand, bias-aware analysis, and objective competency scoring for every candidate."
      seoPath="/meet-jay"
      seoKeywords="AI interviewer, autonomous interview, AI screening, video interview AI, hiring AI, Jay Numuv"
      eyebrow="Your AI interviewer"
      title="Meet Jay — the interviewer who never sleeps"
      subtitle="Jay conducts structured video and audio interviews on demand, then analyzes tone, content, and competency signals to produce an objective, bias-aware report for every candidate."
    >
      <div className="grid md:grid-cols-2 gap-5 md:gap-7 mt-4">
        {[
          { icon: Waves, color: '#6D5EF7', title: 'Video & audio recordings', desc: 'Every session is captured and fully searchable across your whole hiring history.' },
          { icon: Brain, color: '#8B5CF6', title: 'AI-based analysis', desc: 'Competency scoring, instant summaries, gap flagging — with citations back to the exact answer.' },
          { icon: Dumbbell, color: '#12B6A6', title: 'Mock interview prep', desc: 'Applicants rehearse with Jay before the real thing and get feedback on tone and structure.' },
          { icon: Shield, color: '#F59E0B', title: 'Bias-aware by design', desc: 'Jay surfaces signals, never verdicts. Humans always own the decision — Jay just makes it faster and fairer.' },
        ].map((f) => (
          <ContentCard key={f.title} className="!p-6">
            <span className="grid h-12 w-12 place-items-center rounded-[14px]" style={{ background: `${f.color}1F`, color: f.color }}><f.icon className="w-5 h-5" /></span>
            <h3 className="dis mt-4 text-[18px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>{f.title}</h3>
            <p className="mt-2 text-[13.5px] leading-[21px] t3">{f.desc}</p>
          </ContentCard>
        ))}
      </div>

      <ContentCard className="mt-10">
        <div className="flex items-start gap-4">
          <span className="grid h-12 w-12 place-items-center rounded-[14px] aurora text-white shrink-0"><Bot className="w-5 h-5" /></span>
          <div>
            <h3 className="dis text-[20px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>How Jay works</h3>
            <ul className="mt-3 space-y-2.5 text-[14px] t2">
              {[
                'Recruiter defines role, benchmarks, and toughness — or lets Jay generate them.',
                'Jay conducts the interview via video or audio (or both), on the candidate\'s schedule.',
                'Every answer is transcribed with speaker labels and timestamps.',
                'Jay scores communication, technical fit, behavioural signals, and produces a summary.',
                'Recruiter reviews with one-click jumps to specific answers and shares highlights with the team.',
              ].map((s) => (
                <li key={s} className="flex items-start gap-2.5"><Check className="w-4 h-4 mt-0.5 text-[#12B6A6] shrink-0" />{s}</li>
              ))}
            </ul>
          </div>
        </div>
      </ContentCard>
    </MarketingPage>
  );
}
